import "./index.css";
import {
  initBv,
  bvApi,
  type BvSession,
  mountShell,
  statRow,
  dataTable,
  card,
  emptyState,
  pill,
  openModal,
  flash,
  fmtMoney,
  fmtDate,
  skeletonCard,
  h,
} from "./bv-init";

interface Product {
  id: number;
  name: string;
  price: number;
  currency: string;
  sku: string | null;
}
interface Stats {
  sales_today: number;
  takings_today: number;
  sales_total: number;
  products: number;
}
interface Sale {
  id: number;
  total: number;
  currency: string;
  tender: string;
  items: { name: string; price: number; qty: number }[];
  item_count: number;
  created_at: string;
}
interface OnlineOrder {
  ref: string;
  customer: string;
  total: number;
  currency: string;
  at: string | null;
}
interface CartItem {
  id: number;
  name: string;
  price: number;
  qty: number;
}

let session: BvSession;
let currency = "JMD";
let cart: CartItem[] = [];
let tender = "cash";
let cartKey = "pos_cart";

boot();

async function boot() {
  try {
    session = await initBv();
  } catch (err) {
    renderFatal(err);
    return;
  }
  currency = session.merchant.currency_code || "JMD";
  cartKey = `pos_cart_${session.merchant.id || "m"}`;
  try {
    cart = JSON.parse(localStorage.getItem(cartKey) || "[]");
  } catch {
    cart = [];
  }
  mountShell({
    brandIcon: "receipt",
    brandLogo: "/logo.svg",
    title: "Counter POS",
    subtitle: `${session.merchant.name || "Your store"} · quick sale`,
    poweredBy: "Marketplace",
    tabs: [
      { id: "register", label: "Register", icon: "receipt", render: renderRegister },
      { id: "sales", label: "Sales", icon: "list", render: renderSales },
      { id: "products", label: "Products", icon: "tag", render: renderProducts },
    ],
  });
}

function renderFatal(err: unknown) {
  const root = document.getElementById("root")!;
  root.innerHTML = "";
  root.append(
    h(
      "div",
      { class: "bv-fatal" },
      h("strong", null, "Couldn't start"),
      h("p", { class: "bv-muted" }, (err as any)?.message || "No session token found."),
    ),
  );
}

const saveCart = () => localStorage.setItem(cartKey, JSON.stringify(cart));
const cartTotal = () => cart.reduce((s, it) => s + it.price * it.qty, 0);
const cartCount = () => cart.reduce((s, it) => s + it.qty, 0);

function addToCart(p: Product) {
  const ex = cart.find((it) => it.id === p.id);
  if (ex) ex.qty += 1;
  else cart.push({ id: p.id, name: p.name, price: Number(p.price), qty: 1 });
  saveCart();
}
function setQty(id: number, delta: number) {
  const it = cart.find((c) => c.id === id);
  if (!it) return;
  it.qty += delta;
  if (it.qty <= 0) cart = cart.filter((c) => c.id !== id);
  saveCart();
}

async function renderRegister(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const d = await bvApi<{ products: Product[] }>("/api/products").catch(() => ({ products: [] as Product[] }));
  host.innerHTML = "";

  const grid = h("div", {
    style: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "10px" },
  });
  for (const p of d.products) {
    const btn = h(
      "button",
      {
        class: "secondary",
        style: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", padding: "14px", height: "auto", textAlign: "left" },
        onClick: () => {
          addToCart(p);
          paintCart();
        },
      },
      h("span", { style: { fontWeight: "600" } }, p.name),
      h("span", { class: "bv-muted", style: { fontSize: "0.85rem" } }, fmtMoney(Number(p.price), p.currency || currency)),
    );
    grid.append(btn);
  }
  if (!d.products.length) grid.append(emptyState({ icon: "tag", title: "No products", text: "Add products in the Products tab." }));

  const cartPanel = h("div");
  const paintCart = () => {
    cartPanel.innerHTML = "";
    const lines = h("div", { class: "bv-stack", style: { gap: "8px" } });
    if (!cart.length) {
      lines.append(h("p", { class: "bv-muted" }, "Tap products to add them to the sale."));
    } else {
      for (const it of cart) {
        lines.append(
          h(
            "div",
            { class: "bv-row", style: { justifyContent: "space-between", alignItems: "center", gap: "8px" } },
            h("div", { style: { flex: "1", minWidth: "0" } }, h("div", { style: { fontWeight: "600" } }, it.name), h("div", { class: "bv-muted", style: { fontSize: "0.82rem" } }, fmtMoney(it.price, currency))),
            h(
              "div",
              { class: "bv-row", style: { gap: "6px", alignItems: "center" } },
              h("button", { class: "ghost", onClick: () => { setQty(it.id, -1); paintCart(); } }, "−"),
              h("span", { style: { minWidth: "18px", textAlign: "center" } }, String(it.qty)),
              h("button", { class: "ghost", onClick: () => { setQty(it.id, 1); paintCart(); } }, "+"),
            ),
          ),
        );
      }
    }

    const tenders = h(
      "div",
      { class: "bv-row", style: { gap: "6px", marginTop: "6px" } },
      ...["cash", "card", "other"].map((t) =>
        h("button", { class: t === tender ? "secondary" : "ghost", onClick: () => { tender = t; paintCart(); } }, t.charAt(0).toUpperCase() + t.slice(1)),
      ),
    );

    cartPanel.append(
      card({
        title: `Sale · ${cartCount()} item${cartCount() === 1 ? "" : "s"}`,
        body: h(
          "div",
          { class: "bv-stack" },
          lines,
          h("div", { class: "bv-row", style: { justifyContent: "space-between", fontWeight: "700", fontSize: "1.2rem", borderTop: "1px solid var(--line)", paddingTop: "10px" } }, h("span", null, "Total"), h("span", null, fmtMoney(cartTotal(), currency))),
          h("div", { class: "bv-label" }, "Tender"),
          tenders,
          h(
            "div",
            { class: "bv-row", style: { gap: "8px", marginTop: "6px" } },
            h("button", { class: "primary", style: { flex: "2" }, onClick: () => charge(host) }, cart.length ? `Charge ${fmtMoney(cartTotal(), currency)}` : "Charge"),
            h("button", { class: "ghost", onClick: () => { cart = []; saveCart(); paintCart(); } }, "Clear"),
          ),
        ),
      }),
    );
  };
  paintCart();

  const layout = h("div", { style: { display: "grid", gridTemplateColumns: "minmax(0, 1.6fr) minmax(260px, 1fr)", gap: "16px", alignItems: "start" } });
  layout.append(card({ title: "Products", body: grid }), cartPanel);
  host.append(layout);
}

async function charge(host: HTMLElement) {
  if (!cart.length) return flash("Cart is empty", "info");
  const items = cart.map((it) => ({ name: it.name, price: it.price, qty: it.qty }));
  const r = await bvApi<{ sale: Sale }>("/api/sales", { method: "POST", body: JSON.stringify({ items, tender }) }).catch(() => null);
  if (!r) return flash("Sale failed — try again", "error");
  showReceipt(r.sale, items);
  cart = [];
  saveCart();
  renderRegister(host);
}

function showReceipt(sale: Sale, items: { name: string; price: number; qty: number }[]) {
  openModal({
    title: "Sale complete 🧾",
    body: h(
      "div",
      { class: "bv-stack" },
      ...items.map((it) =>
        h("div", { class: "bv-row", style: { justifyContent: "space-between" } }, h("span", null, `${it.qty} × ${it.name}`), h("span", null, fmtMoney(it.price * it.qty, sale.currency || currency))),
      ),
      h("div", { class: "bv-row", style: { justifyContent: "space-between", fontWeight: "700", borderTop: "1px solid var(--line)", paddingTop: "8px" } }, h("span", null, `Total (${sale.tender})`), h("span", null, fmtMoney(Number(sale.total), sale.currency || currency))),
    ),
    actions: [{ label: "New sale", primary: true }],
  });
  flash("Sale recorded", "success");
}

async function renderSales(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const [ov, sales] = await Promise.all([
    bvApi<{ stats: Stats }>("/api/overview").catch(() => null),
    bvApi<{ sales: Sale[] }>("/api/sales").catch(() => ({ sales: [] as Sale[] })),
  ]);
  host.innerHTML = "";
  if (ov) {
    const s = ov.stats;
    host.append(
      statRow([
        { k: "Takings today", v: fmtMoney(Number(s.takings_today), currency), icon: "wallet", tone: "accent" },
        { k: "Sales today", v: String(s.sales_today), icon: "receipt" },
        { k: "Sales total", v: String(s.sales_total), icon: "list" },
        { k: "Products", v: String(s.products), icon: "tag" },
      ]),
    );
  }

  host.append(
    card({
      title: "In-person sales",
      body: sales.sales.length
        ? dataTable<Sale>({
            columns: [
              { head: "When", cell: (r) => fmtDate(r.created_at, true) },
              { head: "Items", num: true, cell: (r) => String(r.item_count) },
              { head: "Tender", cell: (r) => pill(r.tender, r.tender === "cash" ? "ok" : "") },
              { head: "Total", num: true, cell: (r) => fmtMoney(Number(r.total), r.currency || currency) },
            ],
            rows: sales.sales,
          })
        : emptyState({ icon: "receipt", title: "No sales yet", text: "Ring up a sale on the Register tab." }),
    }),
  );

  // Online orders (from Inkress) — lazy loaded
  const onlineBody = h("div", h("p", { class: "bv-muted" }, "Loading recent online orders…"));
  host.append(card({ title: "Online orders (Inkress)", body: onlineBody }));
  bvApi<{ orders: OnlineOrder[] }>("/api/online-orders")
    .then((d) => {
      onlineBody.innerHTML = "";
      onlineBody.append(
        d.orders.length
          ? dataTable<OnlineOrder>({
              columns: [
                { head: "Order", cell: (r) => `#${r.ref}` },
                { head: "Customer", cell: (r) => r.customer },
                { head: "Total", num: true, cell: (r) => fmtMoney(Number(r.total), r.currency || currency) },
              ],
              rows: d.orders,
            })
          : emptyState({ icon: "list", title: "No online orders", text: "Paid online orders will show here." }),
      );
    })
    .catch(() => {
      onlineBody.innerHTML = "";
      onlineBody.append(h("p", { class: "bv-muted" }, "Couldn't load online orders."));
    });
}

async function renderProducts(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const d = await bvApi<{ products: Product[] }>("/api/products").catch(() => ({ products: [] as Product[] }));
  host.innerHTML = "";
  host.append(
    card({
      title: "Products",
      action: h("button", { class: "primary", onClick: () => openProduct(host) }, "New product"),
      body: d.products.length
        ? dataTable<Product>({
            columns: [
              { head: "Name", cell: (p) => p.name },
              { head: "SKU", cell: (p) => p.sku || "—" },
              { head: "Price", num: true, cell: (p) => fmtMoney(Number(p.price), p.currency || currency) },
            ],
            rows: d.products,
            rowActions: (p) =>
              h(
                "div",
                { class: "bv-row" },
                h("button", { class: "ghost", onClick: () => openProduct(host, p) }, "Edit"),
                h("button", { class: "ghost", onClick: () => delProduct(p, host) }, "Remove"),
              ),
          })
        : emptyState({ icon: "tag", title: "No products", text: "Add the items you sell at the counter." }),
    }),
  );
}

function openProduct(host: HTMLElement, p?: Product) {
  const name = h("input", { type: "text", value: p?.name || "" }) as HTMLInputElement;
  const price = h("input", { type: "number", min: "0", step: "0.01", value: String(p?.price ?? 0) }) as HTMLInputElement;
  const sku = h("input", { type: "text", value: p?.sku || "", placeholder: "Optional" }) as HTMLInputElement;
  let close = () => {};
  const handle = openModal({
    title: p ? "Edit product" : "New product",
    body: h("div", { class: "bv-stack" }, field("Name", name), field(`Price (${currency})`, price), field("SKU", sku)),
    actions: [
      { label: "Cancel" },
      {
        label: "Save",
        primary: true,
        onClick: () => {
          if (!name.value.trim()) {
            flash("Name required", "error");
            return true;
          }
          bvApi("/api/products", { method: "POST", body: JSON.stringify({ id: p?.id, name: name.value.trim(), price: +price.value, sku: sku.value.trim() || null }) })
            .then(() => {
              flash("Saved", "success");
              close();
              renderProducts(host);
            })
            .catch((e) => flash(e?.message || "Failed", "error"));
          return true;
        },
      },
    ],
  });
  close = handle.close;
}

async function delProduct(p: Product, host: HTMLElement) {
  const ok = await bvApi(`/api/products/${p.id}/delete`, { method: "POST" }).then(() => true).catch(() => false);
  flash(ok ? `Removed ${p.name}` : "Failed", ok ? "success" : "error");
  if (ok) renderProducts(host);
}

function field(label: string, input: HTMLElement): HTMLElement {
  return h("div", { class: "bv-field" }, h("label", { class: "bv-label" }, label), input);
}
